import type { Contact, RevealBulkResponse } from '../../../domain/contact';
import type { ContactsToExportPayload, ExportToCrmReturn, LeadsToExportPayload } from '../../../domain/crm';
import { Crm } from '../../../domain/crm';

export type PartialSuccessToast = {
  totalCount: number;
  successCount: number;
  link: string;
  revealedSuccessCount?: number;
  revealedTotalCount?: number;
};

export interface ExportContactsToCrmRepository {
  exportAsContacts(payload: ContactsToExportPayload, crm: Crm): Promise<ExportToCrmReturn>;
  exportAsLeads(payload: LeadsToExportPayload): Promise<ExportToCrmReturn>;
  showLoadingToast(count: number, crm: Crm): void;
  showSuccessToast(): void;
  showPartialSuccessToast(config: PartialSuccessToast): void;
  showErrorToast(link?: string): void;
  updateContact: (
    contactId: string,
    linkObject: Pick<Contact, 'hubspotContactUrl' | 'salesforceLeadUrl' | 'salesforceContactUrl'>
  ) => void;
  decreaseExportsQuota(count?: number): void;
}

export type ExportContactsToCrmUseCase = ({
  payload,
  crm,
  onSuccess,
  onError,
  onSettled,
  revealResult,
}: {
  payload: LeadsToExportPayload | ContactsToExportPayload;
  crm: Crm;
  onSuccess: () => void;
  onError: () => void;
  onSettled?: () => void;
  revealResult?: RevealBulkResponse;
}) => Promise<void>;

export const makeExportContactsToCrmUseCase =
  (repository: ExportContactsToCrmRepository): ExportContactsToCrmUseCase =>
  async ({ payload, crm, onSuccess, onError, onSettled, revealResult }) => {
    const isExportedAsLead = 'leads' in payload;
    const requestedCount = isExportedAsLead ? payload.leads.length : payload.contacts.length;

    const createLinkObject = (crmLink: string) => {
      let linkObject;
      if (crm === Crm.hubspot) {
        linkObject = { hubspotContactUrl: crmLink };
      } else if (isExportedAsLead) {
        linkObject = { salesforceLeadUrl: crmLink };
      } else {
        linkObject = { salesforceContactUrl: crmLink };
      }
      return linkObject;
    };

    const buildToast = (
      count: number,
      result: ExportToCrmReturn,
      revealResult?: RevealBulkResponse
    ): PartialSuccessToast => {
      const baseToast: PartialSuccessToast = {
        totalCount: count,
        successCount: result?.processedRecords || 0,
        link: result?.exportHistoryUrl || '',
      };

      if (revealResult) {
        baseToast.revealedSuccessCount = revealResult.totalCount || 0;
        baseToast.revealedTotalCount = (revealResult.totalCount || 0) + (revealResult.notRevealedContactsCount || 0);
      }

      return baseToast;
    };

    try {
      repository.showLoadingToast(requestedCount, crm);

      const result = isExportedAsLead
        ? await repository.exportAsLeads(payload)
        : await repository.exportAsContacts(payload, crm);

      if (requestedCount === 0 || !result || (result.processedRecords === 0 && !revealResult)) {
        repository.showErrorToast(result?.exportHistoryUrl);
        onError();
        return;
      }

      const allExportedSucceeded = result.processedRecords === requestedCount;
      const allRevealedSucceeded = revealResult?.notRevealedContactsIds?.length === 0;
      const toast = buildToast(requestedCount, result, revealResult);

      if (revealResult?.totalCount) {
        repository.decreaseExportsQuota(revealResult.totalCount);
      }

      if ((allExportedSucceeded && !revealResult) || (allExportedSucceeded && allRevealedSucceeded)) {
        repository.showSuccessToast();
      } else {
        repository.showPartialSuccessToast(toast);
      }

      if (result.succeededLinks) {
        result.succeededLinks.forEach((link) => {
          repository.updateContact(link.similarwebId, createLinkObject(link.externalLink));
        });
      }

      onSuccess();
    } catch (error) {
      onError();
      repository.showErrorToast();
    } finally {
      onSettled?.();
    }
  };
